/**
 * A PII-free, low-cardinality description of a caught failure, for the
 * `detail` of a `recordClientFailure` event.
 *
 * `err.name` alone could not separate the two commonest handled failures: a
 * dropped connection and a defect in our own code both arrive as `TypeError`.
 * The 2026-09-13 failure-scan digest carried a prod `workflow-poll-failed` /
 * `TypeError` from an Android browser that, for that reason, could be
 * attributed neither to the visitor's network nor to us.
 *
 * The result is `<error name>:<kind>`. The name stays first so the detail
 * still reads the way earlier reports did; the kind comes from a fixed
 * vocabulary. Message text is never included — it can carry a URL, a filename
 * or response content.
 */

import { ApiRequestError } from '../agents/apiRequestError.ts';

/**
 * What each engine says when a `fetch` gets no response at all. Measured
 * 2026-09-13 with Playwright against a refused connection: Chromium 147
 * "Failed to fetch", Firefox 148 "NetworkError when attempting to fetch
 * resource.", WebKit 26.4 "Load failed". Every other `TypeError` is our own
 * code — reading a property of `undefined`, say.
 */
const FETCH_FAILURE_MESSAGES: readonly string[] = [
  'Failed to fetch',
  'NetworkError when attempting to fetch resource.',
  'Load failed',
];

/**
 * `requestTimeout()` expiring. In the same measurement Chromium and Firefox
 * rejected with `TimeoutError`, while WebKit 26.4 rejected with `AbortError`.
 * No caller of this aborts its requests for any other reason.
 */
const TIMEOUT_NAMES: readonly string[] = ['TimeoutError', 'AbortError'];

/** `<error name>:<kind>` for an error, `unknown` for anything else thrown. */
export function failureKind(err: unknown): string {
  if (!(err instanceof Error) && !(err instanceof DOMException)) {
    return 'unknown';
  }
  return `${err.name}:${kindOf(err)}`;
}

function kindOf(err: Error): string {
  if (err instanceof ApiRequestError) {
    return err.problem;
  }
  if (TIMEOUT_NAMES.includes(err.name)) {
    return 'timeout';
  }
  if (err instanceof TypeError) {
    return FETCH_FAILURE_MESSAGES.some((message) => err.message.startsWith(message))
      ? 'network'
      : 'code';
  }
  if (err instanceof SyntaxError) {
    return 'parse';
  }
  return 'other';
}
